import React from 'react';

interface PulseLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

export const PulseLogo: React.FC<PulseLogoProps> = ({ 
  size = 'md', 
  showText = true, 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  const textSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl'
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Animated Pulse Wave Icon */}
      <div className={`${sizeClasses[size]} relative flex items-center justify-center`}>
        <svg 
          viewBox="0 0 100 100" 
          className="w-full h-full"
          style={{
            filter: 'drop-shadow(0 4px 8px rgba(255, 134, 151, 0.3))'
          }}
        >
          {/* Background gradient */}
          <defs>
            <linearGradient id="pulseGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff8697" />
              <stop offset="50%" stopColor="#ff6b85" />
              <stop offset="100%" stopColor="#ff4d73" />
            </linearGradient>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.9" />
            </linearGradient>
          </defs>
          
          {/* Main circle background */}
          <circle 
            cx="50" 
            cy="50" 
            r="45" 
            fill="url(#pulseGradient)"
            className="animate-pulse"
          />
          
          {/* Pulse wave line */}
          <path 
            d="M 15 50 L 25 50 L 30 35 L 35 65 L 40 20 L 45 80 L 50 50 L 55 30 L 60 70 L 65 40 L 70 60 L 75 50 L 85 50" 
            stroke="url(#waveGradient)" 
            strokeWidth="3" 
            fill="none" 
            strokeLinecap="round"
            strokeLinejoin="round"
            className="animate-pulse"
            style={{
              animation: 'pulse 2s ease-in-out infinite'
            }}
          />
          
          {/* Pulsing dots */}
          <circle cx="30" cy="35" r="2" fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="45" cy="20" r="2" fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle cx="60" cy="70" r="2" fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="2.1s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>
      
      {/* Text */}
      {showText && (
        <div className="flex flex-col">
          <div 
            className={`font-bold ${textSizes[size]} leading-tight`}
            style={{ 
              background: 'linear-gradient(135deg, #ff8697 0%, #ff4d73 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            PULSE
          </div>
          {size !== 'sm' && (
            <div className="text-sm font-medium opacity-80" style={{ color: 'var(--text-secondary)' }}>
              OnPrem Service Monitor
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 